/**
 * Fetches the live results for the session's active activity and keeps them
 * fresh by polling. Used by ZoomCollaborateDisplay to render the display
 * NATIVELY (same origin as the Zoom app) instead of iframing the web display
 * page — Zoom's in-client webview gates a second domain, so the iframe was
 * blocked. Node transitions arrive via props (App.tsx's socket); result tallies
 * refresh on a short poll.
 */

import { useState, useEffect, useRef } from 'react';
import type { SessionNode } from '../lib/api';
import { getSessionState } from '../lib/api';
import {
  ensureParticipant,
  getActivePoll,
  getPollResults,
  getActiveNps,
  getNpsResults,
  getActiveWordCloud,
  getWordCloudResults,
  getActiveQuestion,
  getQuestionResponses,
  getQuestionSummary,
  getActiveQuiz,
  getQuizResults,
  getActiveSwot,
  getSwotResults,
  getSwotSummary,
  getActiveCrosstab,
  getCrossTabResults,
  getActiveExam,
  getActiveImageActivity,
  getImageActivityResults,
  type PollResults,
  type NpsResults,
  type WordCloudResults,
  type QuestionResponse,
  type QuestionSummary,
  type QuizResults,
  type SwotResults,
  type SwotSummary,
  type CrossTabResults,
  type ImageActivityResults,
} from '../lib/participantApi';

export interface DisplayData {
  poll: PollResults | null;
  nps: { question: string; results: NpsResults } | null;
  wordCloud: WordCloudResults | null;
  question: {
    questionId: string;
    prompt: string;
    responses: QuestionResponse[];
    summary: QuestionSummary | null;
  } | null;
  quiz: {
    /** True once the quiz has ended (deactivated) — show standings. */
    complete: boolean;
    currentIndex: number;
    revealed: boolean;
    correctOptionId?: string;
    results: QuizResults;
  } | null;
  swot: SwotResults | null;
  swotSummary: SwotSummary | null;
  crosstab: CrossTabResults | null;
  exam: { title: string; totalQuestions: number } | null;
  imageActivity: ImageActivityResults | null;
  /** Active activity's server timer, for a synced countdown. */
  timer: { expiresAt: string; durationMinutes: number } | null;
}

const EMPTY: DisplayData = {
  poll: null,
  nps: null,
  wordCloud: null,
  question: null,
  quiz: null,
  swot: null,
  swotSummary: null,
  crosstab: null,
  exam: null,
  imageActivity: null,
  timer: null,
};
const POLL_MS = 3000;

export function useActivityResults(
  sessionId: string,
  activeNode: SessionNode | null,
  refreshNonce = 0,
): DisplayData {
  const [data, setData] = useState<DisplayData>(EMPTY);
  const type = activeNode?.type ?? null;
  const nodeId = activeNode?.id ?? null;

  // Remember the quiz's id for the current node so we can still show standings
  // after the quiz deactivates (getActiveQuiz then returns null). Reset only on
  // a real node change, not on a refresh.
  const quizIdRef = useRef<string | null>(null);
  // Clear on a real node change only — NOT on a refresh, so reveal/advance
  // update in place instead of flashing the waiting state.
  useEffect(() => {
    quizIdRef.current = null;
    setData(EMPTY);
  }, [nodeId, type]);

  useEffect(() => {
    if (!sessionId || !type) return;

    let cancelled = false;

    const fetchOnce = async () => {
      try {
        await ensureParticipant(sessionId, 'Display');
        if (cancelled) return;

        // Server timer (best-effort, independent of activity type).
        try {
          const st = await getSessionState(sessionId);
          if (!cancelled)
            setData((d) => ({
              ...d,
              timer: st.timerExpiresAt
                ? { expiresAt: st.timerExpiresAt, durationMinutes: st.timerDurationMinutes ?? 0 }
                : null,
            }));
        } catch {
          /* no timer / not readable — ignore */
        }

        if (type === 'poll') {
          const a = await getActivePoll(sessionId);
          const res = a ? await getPollResults(a._id) : null;
          if (!cancelled) setData((d) => ({ ...d, poll: res }));
        } else if (type === 'nps') {
          const a = await getActiveNps(sessionId);
          const res = a ? await getNpsResults(a._id) : null;
          if (!cancelled)
            setData((d) => ({
              ...d,
              nps: a && res ? { question: a.question, results: res } : null,
            }));
        } else if (type === 'wordcloud') {
          const a = await getActiveWordCloud(sessionId);
          const res = a ? await getWordCloudResults(a._id) : null;
          if (!cancelled) setData((d) => ({ ...d, wordCloud: res }));
        } else if (type === 'question') {
          const a = await getActiveQuestion(sessionId);
          if (a) {
            const [res, summary] = await Promise.all([
              getQuestionResponses(a._id),
              getQuestionSummary(a._id).catch(() => null),
            ]);
            if (!cancelled)
              setData((d) => ({
                ...d,
                question: { questionId: a._id, prompt: a.promptText, responses: res, summary },
              }));
          } else if (!cancelled) {
            setData((d) => ({ ...d, question: null }));
          }
        } else if (type === 'quiz') {
          const a = await getActiveQuiz(sessionId);
          if (a) {
            quizIdRef.current = a._id;
            const res = await getQuizResults(a._id);
            if (!cancelled)
              setData((d) => ({
                ...d,
                quiz: {
                  complete: false,
                  currentIndex: a.currentQuestionIndex,
                  revealed: !!a.revealedAnswer,
                  correctOptionId: a.questions[a.currentQuestionIndex]?.correctOptionId,
                  results: res,
                },
              }));
          } else if (quizIdRef.current) {
            // Quiz ended — keep showing standings from the remembered id.
            const res = await getQuizResults(quizIdRef.current);
            if (!cancelled)
              setData((d) => ({
                ...d,
                quiz: { complete: true, currentIndex: 0, revealed: true, results: res },
              }));
          } else if (!cancelled) {
            setData((d) => ({ ...d, quiz: null }));
          }
        } else if (type === 'swotanalysis') {
          const a = await getActiveSwot(sessionId);
          if (a) {
            const [res, summary] = await Promise.all([
              getSwotResults(a._id),
              getSwotSummary(a._id).catch(() => null),
            ]);
            if (!cancelled) setData((d) => ({ ...d, swot: res, swotSummary: summary }));
          } else if (!cancelled) {
            setData((d) => ({ ...d, swot: null, swotSummary: null }));
          }
        } else if (type === 'crosstab') {
          const a = await getActiveCrosstab(sessionId);
          const res = a ? await getCrossTabResults(a._id) : null;
          if (!cancelled) setData((d) => ({ ...d, crosstab: res }));
        } else if (type === 'exam') {
          const a = await getActiveExam(sessionId);
          if (!cancelled)
            setData((d) => ({
              ...d,
              exam: a ? { title: a.title, totalQuestions: a.questions.length } : null,
            }));
        } else if (type === 'imageactivity') {
          const a = await getActiveImageActivity(sessionId);
          const res = a ? await getImageActivityResults(a._id) : null;
          if (!cancelled) setData((d) => ({ ...d, imageActivity: res }));
        }
        // remaining types render a generic card; nothing to fetch.
      } catch {
        // best-effort — keep the last good data on a transient failure
      }
    };

    fetchOnce();
    const timer = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [sessionId, type, nodeId, refreshNonce]);

  return data;
}
